import React, { useState } from 'react';
import { Alert, Box, Button, Divider, FormControlLabel, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import type { BrandAsset, BrandKit } from '../../../services/editingApi';
import type { EditList, Voiceover } from '../../../utils/editing/editList';
import { EDIT_LIMITS, outputDurationMs } from '../../../utils/editing/editList';
import { addVoiceover, formatMs, newItemId, removeItem, updateItem } from '../editStudioModel';
import { Choice, DbSlider, PanelHint, SecondsField } from './controls';

interface SoundPanelProps {
  list: EditList;
  kit: BrandKit | null;
  outputMs: number;
  onChange: (next: EditList) => void;
  onOpenBrandKit: () => void;
  /** Spoken lines: the voices on offer (empty when TTS isn't available) and how to generate one. */
  voices?: string[];
  onSpeak?: (voiceoverId: string) => Promise<void>;
}

/** The original sound, music under it, and voiceovers on top. */
const SoundPanel: React.FC<SoundPanelProps> = ({ list, kit, outputMs, onChange, onOpenBrandKit, voices = [], onSpeak }) => {
  const [musicMenu, setMusicMenu] = useState<HTMLElement | null>(null);
  const [voiceMenu, setVoiceMenu] = useState<HTMLElement | null>(null);
  const [lineText, setLineText] = useState('');
  const [speaking, setSpeaking] = useState<string | null>(null);
  const [speakProblem, setSpeakProblem] = useState<string | null>(null);
  const addSpokenLine = () => {
    const text = lineText.trim();
    if (!text || voices.length === 0) return;
    const item: Voiceover = { id: newItemId('line'), source: { kind: 'tts', text, voice: voices[0], assetId: null }, atMs: Math.max(0, Math.round(outputMs)), gainDb: 0, duckOriginal: true };
    onChange({ ...list, voiceovers: [...list.voiceovers, item] });
    setLineText('');
  };
  const speak = async (voiceoverId: string) => {
    if (!onSpeak) return;
    setSpeaking(voiceoverId);
    setSpeakProblem(null);
    try {
      await onSpeak(voiceoverId);
    } catch (error) {
      setSpeakProblem(error instanceof Error ? error.message : 'The voice couldn’t be generated.');
    } finally {
      setSpeaking(null);
    }
  };
  const sounds: BrandAsset[] = (kit?.assets || []).filter((asset) => asset.kind === 'audio');
  const total = outputDurationMs(list);
  const music = list.music;
  const name = (id: string | null) => sounds.find((asset) => asset.id === id)?.name || 'Sound';
  const changeMusic = (patch: Partial<NonNullable<EditList['music']>>) => { if (music) onChange({ ...list, music: { ...music, ...patch } }); };
  return (
    <Box>
      <Typography variant="subtitle2">Original sound</Typography>
      <DbSlider label="Level" value={list.audio.originalGainDb} min={EDIT_LIMITS.gainDb.min} max={EDIT_LIMITS.gainDb.max} onChange={(originalGainDb) => onChange({ ...list, audio: { ...list.audio, originalGainDb } })} />
      <FormControlLabel control={<Switch checked={list.audio.normalize} onChange={(event) => onChange({ ...list, audio: { ...list.audio, normalize: event.target.checked } })} />} label={<Typography variant="body2">Even out the loudness at the end</Typography>} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Music</Typography>
      <PanelHint>A track from your brand kit under the whole result, lowered while people speak.</PanelHint>
      {music ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{name(music.assetId)}</Typography>
            <Button size="small" onClick={(event) => setMusicMenu(event.currentTarget)}>Change</Button>
            <IconButton size="small" aria-label="Remove music" onClick={() => onChange({ ...list, music: null })}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
          </Stack>
          <DbSlider label="Level" value={music.gainDb} min={-40} max={0} onChange={(gainDb) => changeMusic({ gainDb })} />
          <FormControlLabel control={<Switch checked={music.ducking} onChange={(event) => changeMusic({ ducking: event.target.checked })} />} label={<Typography variant="body2">Lower it while people speak</Typography>} />
          {music.ducking ? <DbSlider label="By" value={music.duckDb} min={EDIT_LIMITS.duckDb.min} max={EDIT_LIMITS.duckDb.max} onChange={(duckDb) => changeMusic({ duckDb })} /> : null}
          <FormControlLabel control={<Switch checked={music.loop} onChange={(event) => changeMusic({ loop: event.target.checked })} />} label={<Typography variant="body2">Repeat it to the end</Typography>} />
          <Stack direction="row" spacing={1}>
            <SecondsField label="Starts at" valueMs={music.startMs} maxMs={total} onChange={(startMs) => { if (startMs !== null) changeMusic({ startMs }); }} />
            <SecondsField label="Fade in" valueMs={music.fadeInMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeInMs) => { if (fadeInMs !== null) changeMusic({ fadeInMs }); }} />
            <SecondsField label="Fade out" valueMs={music.fadeOutMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeOutMs) => { if (fadeOutMs !== null) changeMusic({ fadeOutMs }); }} />
          </Stack>
        </Stack>
      ) : (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={(event) => (sounds.length ? setMusicMenu(event.currentTarget) : onOpenBrandKit())} data-testid="add-music">
          {sounds.length ? 'Add music' : 'Upload music first'}
        </Button>
      )}
      <Menu open={Boolean(musicMenu)} anchorEl={musicMenu} onClose={() => setMusicMenu(null)}>
        {sounds.map((asset) => (
          <MenuItem key={asset.id} onClick={() => { onChange({ ...list, music: { assetId: asset.id, startMs: music?.startMs ?? 0, gainDb: music?.gainDb ?? -14, ducking: music?.ducking ?? true, duckDb: music?.duckDb ?? 12, fadeInMs: music?.fadeInMs ?? 1000, fadeOutMs: music?.fadeOutMs ?? 2000, loop: music?.loop ?? true } }); setMusicMenu(null); }}>
            {asset.name}{asset.durationMs ? ` · ${formatMs(asset.durationMs)}` : ''}
          </MenuItem>
        ))}
      </Menu>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Voiceover</Typography>
      <PanelHint>A recording from your brand kit placed at the playhead, or a line spoken by a computer voice.</PanelHint>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers} onClick={(event) => (sounds.length ? setVoiceMenu(event.currentTarget) : onOpenBrandKit())} data-testid="add-voiceover">
          {sounds.length ? 'Add a recording here' : 'Upload a recording first'}
        </Button>
      </Stack>
      {voices.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
          <TextField size="small" fullWidth label="A line to speak here" value={lineText} onChange={(event) => setLineText(event.target.value)} inputProps={{ maxLength: EDIT_LIMITS.maxTtsTextLength, 'data-testid': 'tts-text' }} multiline maxRows={3} />
          <Button size="small" variant="outlined" onClick={addSpokenLine} disabled={!lineText.trim() || list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers} data-testid="add-tts">Add</Button>
        </Stack>
      ) : null}
      {speakProblem ? <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setSpeakProblem(null)}>{speakProblem}</Alert> : null}
      <Menu open={Boolean(voiceMenu)} anchorEl={voiceMenu} onClose={() => setVoiceMenu(null)}>
        {sounds.map((asset) => (
          <MenuItem key={asset.id} onClick={() => { onChange(addVoiceover(list, asset.id, outputMs)); setVoiceMenu(null); }}>{asset.name}</MenuItem>
        ))}
      </Menu>
      <List dense disablePadding sx={{ mt: 1 }}>
        {list.voiceovers.map((voiceover) => (
          <ListItem key={voiceover.id} disableGutters sx={{ display: 'block', py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemText
                primary={voiceover.source.kind === 'tts' ? `“${voiceover.source.text}”` : name(voiceover.source.assetId)}
                secondary={`at ${formatMs(voiceover.atMs)}${voiceover.source.kind === 'tts' && !voiceover.source.assetId ? ' · not generated yet' : ''}`}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <IconButton size="small" aria-label="Remove voiceover" onClick={() => onChange({ ...list, voiceovers: removeItem(list.voiceovers, voiceover.id) })}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <SecondsField label="At" valueMs={voiceover.atMs} maxMs={total} onChange={(atMs) => { if (atMs !== null) onChange({ ...list, voiceovers: updateItem(list.voiceovers, voiceover.id, { atMs }) }); }} />
              <Box sx={{ flex: 1 }}><DbSlider label="Level" value={voiceover.gainDb} min={-20} max={12} onChange={(gainDb) => onChange({ ...list, voiceovers: updateItem(list.voiceovers, voiceover.id, { gainDb }) })} /></Box>
            </Stack>
            <FormControlLabel control={<Switch size="small" checked={voiceover.duckOriginal} onChange={(event) => onChange({ ...list, voiceovers: updateItem(list.voiceovers, voiceover.id, { duckOriginal: event.target.checked }) })} />} label={<Typography variant="caption">Lower the original while it plays</Typography>} />
            {voiceover.source.kind === 'tts' ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Box sx={{ width: 150 }}>
                  <Choice label="Voice" value={voiceover.source.voice} options={(voices.includes(voiceover.source.voice) || !voices.length ? voices.length ? voices : [voiceover.source.voice] : [voiceover.source.voice, ...voices]).map((voice) => ({ value: voice, label: voice.charAt(0).toUpperCase() + voice.slice(1) }))} onChange={(voice) => onChange({ ...list, voiceovers: updateItem(list.voiceovers, voiceover.id, { source: { ...voiceover.source, kind: 'tts', voice, assetId: null } as Voiceover['source'] }) })} />
                </Box>
                <Button size="small" variant={voiceover.source.assetId ? 'text' : 'contained'} disabled={!onSpeak || speaking !== null} onClick={() => speak(voiceover.id)} data-testid={`speak-${voiceover.id}`}>
                  {speaking === voiceover.id ? 'Generating…' : voiceover.source.assetId ? 'Generate again' : 'Generate the voice'}
                </Button>
              </Stack>
            ) : null}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default SoundPanel;
